/**
 * Solana RPC Client — Privacy-Hardened
 * =====================================================================
 * SFN privacy protocol: this module is the ONLY sanctioned path for
 * Solana RPC traffic from the frontend. It enforces a strict allow-list
 * of RPC methods at runtime and refuses any call that could enumerate,
 * resolve, or inspect individual wallets or holder balances.
 *
 * Permitted methods (contract-level reads only):
 *   - getAccountInfo  (used against token MINT accounts)
 *   - getMintInfo     (alias surfaced by some clients)
 *   - getSlot, getBlockHeight, getVersion, getHealth (network status)
 *
 * Forbidden methods (HARD BLOCK, do not extend without security review):
 *   - getTokenAccountsByOwner
 *   - getTokenAccountsByDelegate
 *   - getTokenAccountBalance
 *   - getTokenLargestAccounts
 *   - getProgramAccounts
 *   - getMultipleAccounts (when used to bulk-resolve wallet accounts)
 *   - getSignaturesForAddress
 *   - getConfirmedSignaturesForAddress2
 *   - getParsedTokenAccountsByOwner
 * =====================================================================
 */

const ENV_RPC_URL =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_SOLANA_RPC_URL) ||
  "";

/** Canonical endpoint used for cluster derivation and as last-resort target. */
export const SOLANA_RPC_URL = ENV_RPC_URL || "https://api.mainnet-beta.solana.com";

/**
 * Server-side endpoint chain. `api.mainnet-beta.solana.com` rejects browser
 * origins (HTTP 403) and rate-limits aggressively, which is why treasury
 * reads previously reported "unavailable (RPC)". A dedicated provider
 * (Helius / QuickNode) can be supplied via SOLANA_RPC_URL / HELIUS_RPC_URL
 * and is always tried first; free community endpoints act as fallbacks.
 */
export const RPC_FALLBACK_ENDPOINTS: string[] = [
  "https://solana-rpc.publicnode.com",
  "https://rpc.ankr.com/solana",
  "https://api.mainnet-beta.solana.com",
];

/** Same-origin proxy — keeps browser traffic off origin-blocked public RPCs. */
export const RPC_PROXY_PATH = "/api/public/solana-rpc";

/** Where `solanaRpcCall` sends traffic by default in the current runtime. */
export function defaultRpcTarget(): string {
  if (ENV_RPC_URL) return ENV_RPC_URL;
  if (typeof window !== "undefined") return RPC_PROXY_PATH;
  return RPC_FALLBACK_ENDPOINTS[0];
}

export type SolanaCluster = "mainnet-beta" | "devnet" | "testnet" | "localnet" | "custom";

/** Cluster derived from the configured RPC endpoint — single source of truth. */
export const SOLANA_CLUSTER: SolanaCluster = (() => {
  const u = SOLANA_RPC_URL.toLowerCase();
  if (u.includes("devnet")) return "devnet";
  if (u.includes("testnet")) return "testnet";
  if (u.includes("localhost") || u.includes("127.0.0.1")) return "localnet";
  if (u.includes("mainnet")) return "mainnet-beta";
  return "custom";
})();

/** Human label used in wallet UI surfaces. */
export const SOLANA_CLUSTER_LABEL: Record<SolanaCluster, string> = {
  "mainnet-beta": "Mainnet Beta",
  devnet: "Devnet",
  testnet: "Testnet",
  localnet: "Localnet",
  custom: "Custom RPC",
};

/**
 * Best-effort cluster detection from any RPC endpoint / network name.
 * Returns null when the value is empty or unrecognisable.
 */
export function clusterFromEndpoint(value?: string | null): SolanaCluster | null {
  if (!value) return null;
  const u = String(value).toLowerCase();
  if (u.includes("devnet")) return "devnet";
  if (u.includes("testnet")) return "testnet";
  if (u.includes("localhost") || u.includes("127.0.0.1") || u.includes("localnet"))
    return "localnet";
  if (u.includes("mainnet")) return "mainnet-beta";
  return null;
}

/** Solscan explorer URL for an account, cluster-aware. */
export function solscanAccountUrl(
  address: string,
  cluster: SolanaCluster = SOLANA_CLUSTER,
): string {
  const base = `https://solscan.io/account/${address}`;
  if (cluster === "devnet") return `${base}?cluster=devnet`;
  if (cluster === "testnet") return `${base}?cluster=testnet`;
  if (cluster === "localnet") return `${base}?cluster=custom`;
  return base;
}

export const PERMITTED_RPC_METHODS = new Set<string>([
  "getAccountInfo",
  "getMintInfo",
  // Lamport balance of a *known project account* (treasury transparency).
  // Still cannot enumerate holders — it needs an explicit address and
  // returns no token-account graph.
  "getBalance",
  // Settlement path (real SOL / USDC transfers to the treasury):
  // none of these can enumerate holders — they need an explicit, already
  // user-authorised transaction or signature.
  "getLatestBlockhash",
  "sendTransaction",
  "sendRawTransaction",
  "getSignatureStatuses",
  // Settlement audit of one explicit, user-authorised signature.
  "getTransaction",
  "getSlot",
  "getBlockHeight",
  "getVersion",
  "getHealth",
]);

export const FORBIDDEN_RPC_METHODS = new Set<string>([
  "getTokenAccountsByOwner",
  "getParsedTokenAccountsByOwner",
  "getTokenAccountsByDelegate",
  "getTokenAccountBalance",
  "getTokenLargestAccounts",
  "getProgramAccounts",
  "getMultipleAccounts",
  "getSignaturesForAddress",
  "getConfirmedSignaturesForAddress2",
]);

export const PRIVACY_VIOLATION_MESSAGE =
  "SECURITY ALERT: Wallet tracking is strictly prohibited by SFN privacy protocols.";

export class PrivacyViolationError extends Error {
  constructor(method: string) {
    super(`${PRIVACY_VIOLATION_MESSAGE} (blocked method: ${method})`);
    this.name = "PrivacyViolationError";
  }
}

export type RpcCallOptions = {
  signal?: AbortSignal;
  rpcUrl?: string;
  fetchImpl?: typeof fetch;
};

/**
 * Raised when the same-origin proxy is rate-limiting the caller or its
 * circuit breaker is open because every upstream is degraded. Callers can
 * distinguish "temporarily unavailable, retry later" from a hard failure and
 * render a graceful fallback instead of an error.
 */
export class RpcDegradedError extends Error {
  readonly code: "rate_limited" | "circuit_open";
  readonly retryAfterMs: number;
  constructor(code: "rate_limited" | "circuit_open", retryAfterMs: number) {
    super(
      code === "rate_limited"
        ? "Too many network requests — please retry in a moment."
        : "Solana network reads are temporarily unavailable.",
    );
    this.name = "RpcDegradedError";
    this.code = code;
    this.retryAfterMs = retryAfterMs;
  }
}

/** Browser-only telemetry hook — consumed by src/lib/reliability.ts. */
export const RPC_FAILURE_EVENT = "sfn:rpc-failure";

export type RpcFailureDetail = {
  method: string;
  code: "rate_limited" | "circuit_open" | "http_error" | "rpc_error" | "unreachable";
  status?: number;
};

function emitRpcFailure(detail: RpcFailureDetail): void {
  try {
    if (typeof window === "undefined" || typeof CustomEvent !== "function") return;
    window.dispatchEvent(new CustomEvent(RPC_FAILURE_EVENT, { detail }));
  } catch {
    /* telemetry must never break an RPC read */
  }
}

/**
 * Sole entrypoint for Solana RPC. Any call with a non-permitted method
 * fails immediately, logs to the developer console, and never reaches
 * the network.
 */
export async function solanaRpcCall<T = unknown>(
  method: string,
  params: unknown[],
  options: RpcCallOptions = {},
): Promise<T> {
  if (FORBIDDEN_RPC_METHODS.has(method) || !PERMITTED_RPC_METHODS.has(method)) {
    // Production-quiet: never surface internal security-alert structures
    // to end-user browser consoles. Dev/test still get the actionable warning.
    const env =
      (typeof process !== "undefined" && process.env?.NODE_ENV) ||
      (typeof import.meta !== "undefined" &&
        (import.meta as { env?: Record<string, string> }).env?.MODE) ||
      "development";
    if (env !== "production") {
      console.warn(PRIVACY_VIOLATION_MESSAGE, { attemptedMethod: method });
    }
    throw new PrivacyViolationError(method);
  }

  const fetchImpl = options.fetchImpl ?? fetch;
  const body = JSON.stringify({ jsonrpc: "2.0", id: 1, method, params });

  // Explicit target (tests, custom provider) → single attempt.
  // Otherwise walk the endpoint chain until one answers.
  const targets = options.rpcUrl
    ? [options.rpcUrl]
    : typeof window !== "undefined" || ENV_RPC_URL
      ? [defaultRpcTarget()]
      : RPC_FALLBACK_ENDPOINTS;

  let lastError: unknown = new Error("RPC unreachable");
  let lastCode: RpcFailureDetail["code"] = "unreachable";
  let lastStatus: number | undefined;
  for (const url of targets) {
    try {
      const res = await fetchImpl(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: options.signal,
      });
      if (res.status === 429 || res.status === 503) {
        const retryAfter = Number(res.headers?.get?.("Retry-After") ?? 0) || 5;
        const code = res.status === 429 ? "rate_limited" : "circuit_open";
        emitRpcFailure({ method, code, status: res.status });
        throw new RpcDegradedError(code, retryAfter * 1000);
      }
      if (!res.ok) {
        lastCode = "http_error";
        lastStatus = res.status;
        throw new Error(`RPC HTTP ${res.status}`);
      }
      const json = (await res.json()) as { result?: T; error?: { message?: string } };
      if (json.error) {
        lastCode = "rpc_error";
        throw new Error(json.error.message ?? "RPC error");
      }
      return json.result as T;
    } catch (err) {
      if (options.signal?.aborted) throw err;
      // Degraded responses are terminal — retrying hammers a struggling proxy.
      if (err instanceof RpcDegradedError) throw err;
      lastError = err;
    }
  }
  emitRpcFailure({ method, code: lastCode, ...(lastStatus ? { status: lastStatus } : {}) });
  throw lastError instanceof Error ? lastError : new Error("RPC error");
}

export type MintInfo = {
  mintAuthority: string | null;
  freezeAuthority: string | null;
  supply: string;
  decimals: number;
};

/**
 * Read a token MINT account. Contract-level metadata only —
 * never accepts an arbitrary wallet/holder address.
 */
export async function fetchMintInfo(
  mintAddress: string,
  signal?: AbortSignal,
  options: Omit<RpcCallOptions, "signal"> = {},
): Promise<MintInfo> {
  const result = await solanaRpcCall<{
    value?: { data?: { parsed?: { info?: Record<string, unknown> } } };
  }>("getAccountInfo", [mintAddress, { encoding: "jsonParsed", commitment: "confirmed" }], {
    ...options,
    signal,
  });
  const parsed = result?.value?.data?.parsed?.info;
  if (!parsed) throw new Error("Mint account not found or unparsable");
  return {
    mintAuthority: (parsed.mintAuthority as string | null) ?? null,
    freezeAuthority: (parsed.freezeAuthority as string | null) ?? null,
    supply: (parsed.supply as string) ?? "0",
    decimals: Number(parsed.decimals ?? 0),
  };
}
